// Sprint 20.6 PR-2 (D013-EC-72) — runner-side SESSION_EXPIRED banner.
//
// Rendered above the composer in `ChatView` when the runner emits
// `agent.error({code: 'SESSION_EXPIRED'})` because its respawn-on-startup
// loop attempted `claude -p --resume <sessionId>` but Anthropic returned
// session-not-found. The agent's status has already been flipped to
// `error` by SessionManager; this banner is the founder-mode recovery
// affordance on top of that.
//
// Crucial difference vs SessionLostBanner:
//
//   - SessionLost  → "Retry" CTA. The runner will try `--resume` again
//     on the next user_message; sometimes Anthropic accepts the second
//     attempt (rare but real).
//   - SessionExpired → "Start a new agent" CTA. Anthropic explicitly
//     said the session is gone; retrying with the same sessionId is
//     guaranteed to fail. The next-best founder action is to launch a
//     fresh conversation. The previous turns are still visible in the
//     chat (DB-persisted), so the user does not lose history — only
//     the chained reasoning context is gone.
//
// Affordances:
//   • Start a new agent → opens the composer modal with a blank draft.
//     The user can re-paste the last message and continue the spirit
//     of the conversation in a fresh session.
//   • Dismiss (×) → silent local close. Re-renders if the runner emits
//     another SESSION_EXPIRED.
//
// Founder voice (per founder-first-vision §5): "Esta sesión expiró tras
// estar inactiva. Lanza un agente nuevo para continuar." — calm,
// declarative, with a next action. No jargon, no "404", no
// "Anthropic API returned 404 session_not_found".

import { useTranslation } from '../../i18n';
import { useAppStore } from '../../stores/app.store';

export interface SessionExpiredBannerProps {
  agentId: string;
  message: string | null;
}

export function SessionExpiredBanner({ agentId, message }: SessionExpiredBannerProps) {
  const { t } = useTranslation();
  const clearSessionExpired = useAppStore((s) => s.clearSessionExpired);
  const openComposer = useAppStore((s) => s.openComposer);

  const handleNewAgent = (): void => {
    // Open the new-agent composer in its blank state. The user can
    // re-paste their last message verbatim if they want continuity;
    // we deliberately do NOT pre-populate it because copy-pasting
    // the prompt across sessions is a content decision the founder
    // owns, not the system.
    clearSessionExpired(agentId);
    openComposer();
  };
  const handleDismiss = (): void => {
    clearSessionExpired(agentId);
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      data-testid="session-expired-banner"
      data-agent-id={agentId}
      className="mx-3 mt-2 mb-2 flex items-start justify-between gap-3 rounded-md border border-status-error/30 bg-status-error/10 px-3 py-2 text-body text-foreground"
    >
      <div className="flex flex-col gap-1 min-w-0">
        <span className="text-status-error font-medium">
          {t('sessionExpiredBanner.title')}
        </span>
        <span className="text-foreground/80 text-caption">
          {message ? message : t('sessionExpiredBanner.subtitle')}
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={handleNewAgent}
          data-testid="session-expired-new-agent-btn"
          className="px-3 py-1 rounded-md bg-status-error/20 text-status-error hover:bg-status-error/30 disabled:opacity-60 text-caption font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-status-error/40"
        >
          {t('sessionExpiredBanner.newAgent')}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t('sessionExpiredBanner.dismissAria')}
          data-testid="session-expired-dismiss"
          className="text-foreground/60 hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-status-error/40 rounded-sm px-1"
        >
          {t('sessionExpiredBanner.dismissGlyph')}
        </button>
      </div>
    </div>
  );
}
